import { Controller } from "@hotwired/stimulus"
import ChartsCustomFunctions from "../src/javascripts/nowsignage/charts-custom-functions";
import { AgCharts, LicenseManager } from "ag-charts-enterprise";

LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090595}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{NowSignage}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{NowSignage}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{NowSignage}_need_to_be_licensed___{NowSignage}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{26_June_2026}____[v3]_[0102]_MTc4MjQyODQwMDAwMA==3b13b2ef80749631c8486a224fefaacd");

export default class extends Controller {
  static targets = ["chart"];
  static values = {
    series: { type: Array, default: [] },
    data: { type: Array, default: [] },
    title: { type: Object, default: {} },
    subtitle: { type: Object, default: {} },
    animation: { type: Boolean, default: true },
    palette: { type: Array, default: null },
    linkPath: { type: String, default: '' },
    zoom: { type: Object, default: { enabled: false } },
    navigator: { type: Object, default: { enabled: false } },
    extraOptions: { type: Object, default: {} },
    chartType: { type: String, default: 'default' },
    value: { type: Number, default: null }
  }

  connect() {
    // Gauge charts use 'value', regular charts use 'data' or 'series'
    const isGauge = this.chartTypeValue === 'radial-gauge';
    const hasRegularChartData = this.dataValue.length > 0 || this.seriesValue.length > 0;
    const hasGaugeValue = this.valueValue !== null;

    if (!isGauge && !hasRegularChartData) {
      console.error(`No data or series provided for ${this.element.id} chart`);
      return;
    }

    if (isGauge && !hasGaugeValue) {
      console.error(`No value provided for ${this.element.id} gauge chart`);
      return;
    }

    this.initializeTheme();
    
    this.chartOptions = {
      theme: this.chartTheme,
      container: this.chartTarget,
      ...(isGauge ? { type: "radial-gauge" } : {} ),
      ...(!isGauge ? { data: this.dataValue } : {}),
      ...(!isGauge ? { series: this.resolveFunctions(this.seriesValue) } : {}),
      ...(isGauge && hasGaugeValue ? { value: this.valueValue } : {}),
      title: this.titleValue,
      subtitle: this.subtitleValue,
      animation: {
        enabled: this.animationValue
      },
      ...(!isGauge ? { listeners: { seriesNodeClick: this.handleNodeClick.bind(this) } } : {}),
      ...this.resolveFunctions(this.extraOptionsValue)
    }
    
    this.createChart(true);
    
    this.setupThemeObserver();
  }

  createChart(isInitialLoad = false) { 
    const themeMode = document.body.getAttribute('data-ag-theme-mode');
    this.chartOptions.theme = themeMode === 'dark' ? this.chartThemeDark : this.chartThemeLight;

    if(!isInitialLoad) {
      this.chartOptions.animation.enabled = false;
    }
    
    if (this.chart) {
      this.chart.destroy();
    }

    // console.log(this.chartOptions);

    if(this.chartTypeValue === 'radial-gauge') {
      this.chart = AgCharts.createGauge(this.chartOptions);
    } else {
      this.chart = AgCharts.create(this.chartOptions);
    }
  }

  // Recursively find 'formatter' and 'renderer' keys at any level and replace the string values with actual functions
  resolveFunctions(obj) {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    // Preserve array vs object structure
    const resolved = Array.isArray(obj) ? [] : {};

    for (const [key, value] of Object.entries(obj)) {
      // If we find a formatter/renderer key with string value - replace with function.
      if ((key === 'formatter' || key === 'renderer' || key === 'itemStyler') && typeof value === 'string' && ChartsCustomFunctions[value]) {
        // Pass sibling keys (e.g. translation labels) as second argument so custom functions can accept (params, options).
        const fn = ChartsCustomFunctions[value];
        const options = { ...obj };
        delete options[key];

        resolved[key] = Object.keys(options).length > 0 ? (params) => fn(params, options) : fn;
      } else if (typeof value === 'object' && value !== null) {
        // Recursively process nested objects/arrays to find formatters/renderers at deeper levels
        resolved[key] = this.resolveFunctions(value);
      } else {
        // Keep all other primitive values as-is
        resolved[key] = value;
      }
    }

    return resolved;
  }

  initializeTheme() {
    const existingMode = document.body.getAttribute('data-ag-theme-mode');
    
    if (!existingMode) {
      const themeValue = document.body.getAttribute('data-themes-theme-value');

      if (themeValue) {
        document.body.setAttribute('data-ag-theme-mode', themeValue);
      } else {
        const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        const systemTheme = isDarkMode ? 'dark' : 'light';
        document.body.setAttribute('data-ag-theme-mode', systemTheme);
      }
    }
    
    this.myTheme = {
      baseTheme: 'ag-default',
      params: {
        fontFamily: "Geist",
        fontSize: 14,
        fontWeight: 400,
      }
    };

    // Apply custom palette if provided
    if (this.paletteValue && this.paletteValue.length > 0) {
      this.myTheme.palette = {
        fills: this.paletteValue,
        strokes: this.paletteValue
      };
    }

    this.chartThemeLight = {
      ...this.myTheme,
      baseTheme: 'ag-default',
      params: {
        ...this.myTheme.params,
        foregroundColor: "#1F2937",
        backgroundColor: "#ffffff",
        tooltipBackgroundColor: "#ffffff",
        tooltipTextColor: "#333333",
      }
    };

    this.chartThemeDark = {
      ...this.myTheme,
      baseTheme: 'ag-default-dark',
      params: {
        ...this.myTheme.params,
        foregroundColor: "#ffffff",
        backgroundColor: "#1c1c21",
        // tooltipBackgroundColor: "#2a2a31",
        // tooltipTextColor: "#ffffff",
      }
    };

    const themeMode = document.body.getAttribute('data-ag-theme-mode');
    this.chartTheme = themeMode === 'dark' ? this.chartThemeDark : this.chartThemeLight;
  }

  setupThemeObserver() {
    // Watch for changes to the data-ag-theme-mode attribute on body as AG Charts does not support automatic theme change detection
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ag-theme-mode') {
          this.createChart(false);
        }
      });
    });

    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-ag-theme-mode']
    });
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  handleNodeClick(event) {
    const { datum, xKey, yKey } = event;
  
    if (this.linkPathValue) {
      const url = new URL(this.linkPathValue, window.location.origin);
      if (url.searchParams.has('search[last_connected_at]') && datum && datum.date) {
        url.searchParams.set('search[last_connected_at]', datum.date);
      }
      if (url.searchParams.has('filter_by')) {
        const dim = url.searchParams.get('filter_by');
        if ((dim === 'xKey' || dim === 'x') && datum[xKey]) {
          url.searchParams.set('filter_by', datum[xKey]);
        } else if ((dim === 'yKey' || dim === 'y') && datum[yKey]) {
          url.searchParams.set('filter_by', datum[yKey]);
        }
      }
  
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    }
  }

}